// F18 — API client for the admin portal.
//
// Two API clients:
// 1. adminApi — talks to the eat-admin Worker (CRUD operations)
// 2. opsApi — talks to the eat-navigation Worker (ops triggers)
//
// Both use the X-Admin-Token header for authentication.
// In local dev, the Vite proxy forwards /v1/admin/* to the admin Worker on port 8787.
// Ops trigger calls go to the public Worker (configured via VITE_OPS_API_URL).

const ADMIN_TOKEN_KEY = 'eat-admin-token';

/** Get the admin token from localStorage (set on the login page). */
export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

/** Set the admin token in localStorage. */
export function setAdminToken(token: string): void {
  localStorage.setItem(ADMIN_TOKEN_KEY, token);
}

/** Clear the admin token from localStorage. */
export function clearAdminToken(): void {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
}

/**
 * Fetch the admin token from the Pages Function (/api/admin-token).
 * The Pages Function verifies the Cloudflare Access JWT and returns ADMIN_TOKEN.
 * In local dev (no Access), this returns 401 — user falls back to manual Login.
 */
export async function fetchAdminTokenFromAccess(): Promise<boolean> {
  try {
    const resp = await fetch('/api/admin-token', { credentials: 'same-origin' });
    if (!resp.ok) return false;
    const data = (await resp.json()) as { token?: string };
    if (data.token) {
      setAdminToken(data.token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Base URL for the admin Worker API. In dev, the Vite proxy handles this. */
const ADMIN_API_BASE = import.meta.env.VITE_ADMIN_API_URL ?? '';
/** Base URL for the public Worker ops trigger endpoints. */
const OPS_API_BASE = import.meta.env.VITE_OPS_API_URL ?? '';

/** Common fetch wrapper that adds the X-Admin-Token header and handles errors. */
async function apiFetch(
  baseUrl: string,
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const token = getAdminToken();
  const headers = new Headers(options.headers);
  headers.set('content-type', 'application/json');
  if (token) headers.set('x-admin-token', token);

  const resp = await fetch(`${baseUrl}${path}`, { ...options, headers });

  // If we get a 401, clear the token and redirect to login.
  if (resp.status === 401) {
    clearAdminToken();
    // Redirect to login page if we're in the browser.
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new ApiError(401, 'unauthorized', 'Invalid or missing admin token');
  }

  return resp;
}

/** API error class. */
export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Safely extract an error message from an unknown caught value. */
export function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Unknown error';
}

/** Parse an error response into an ApiError. */
async function parseError(resp: Response): Promise<ApiError> {
  try {
    const body = await resp.json();
    const err = body.error ?? body;
    const message = typeof err.message === 'string' ? err.message : `HTTP ${resp.status}`;
    return new ApiError(resp.status, err.code ?? 'unknown', message);
  } catch {
    return new ApiError(resp.status, 'unknown', `HTTP ${resp.status}`);
  }
}

// ---- Admin Worker API (CRUD) ----

export const adminApi = {
  /** GET /v1/admin/dashboard — dashboard metrics. */
  async getDashboard(): Promise<DashboardData> {
    const resp = await apiFetch(ADMIN_API_BASE, '/v1/admin/dashboard');
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  // ---- LiveOps ----

  /** GET /v1/admin/liveops — list all events. */
  async listLiveops(status?: 'all' | 'active' | 'inactive'): Promise<{ events: LiveopsEvent[]; limit: number; offset: number }> {
    const query = status ? `?status=${status}` : '';
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/liveops${query}`);
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** POST /v1/admin/liveops — create event. */
  async createLiveops(body: CreateLiveopsBody): Promise<LiveopsEvent> {
    const resp = await apiFetch(ADMIN_API_BASE, '/v1/admin/liveops', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** PUT /v1/admin/liveops/:id — update event. */
  async updateLiveops(id: string, body: Partial<CreateLiveopsBody>): Promise<LiveopsEvent> {
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/liveops/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** DELETE /v1/admin/liveops/:id — soft delete event. */
  async deleteLiveops(id: string): Promise<{ deleted: boolean }> {
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/liveops/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** GET /v1/admin/liveops/:id/performance — event performance. */
  async getLiveopsPerformance(id: string): Promise<LiveopsPerformance> {
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/liveops/${encodeURIComponent(id)}/performance`);
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  // ---- Cosmetics ----

  /** GET /v1/admin/cosmetics — list all cosmetics. */
  async listCosmetics(type?: string): Promise<{ cosmetics: Cosmetic[] }> {
    const query = type ? `?type=${type}` : '';
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/cosmetics${query}`);
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** POST /v1/admin/cosmetics — create cosmetic. */
  async createCosmetic(body: CreateCosmeticBody): Promise<{ cosmetic: Cosmetic }> {
    const resp = await apiFetch(ADMIN_API_BASE, '/v1/admin/cosmetics', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** PUT /v1/admin/cosmetics/:id — update cosmetic. */
  async updateCosmetic(id: string, body: Partial<CreateCosmeticBody>): Promise<{ cosmetic: Cosmetic }> {
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/cosmetics/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** DELETE /v1/admin/cosmetics/:id — soft delete cosmetic. */
  async deleteCosmetic(id: string): Promise<{ deleted: boolean }> {
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/cosmetics/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  // ---- Bundles ----

  /** GET /v1/admin/bundles — list all bundles. */
  async listBundles(): Promise<{ bundles: Bundle[] }> {
    const resp = await apiFetch(ADMIN_API_BASE, '/v1/admin/bundles');
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** POST /v1/admin/bundles — create bundle. */
  async createBundle(body: CreateBundleBody): Promise<{ bundle: Bundle }> {
    const resp = await apiFetch(ADMIN_API_BASE, '/v1/admin/bundles', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** PUT /v1/admin/bundles/:id — update bundle. */
  async updateBundle(id: string, body: Partial<CreateBundleBody>): Promise<{ bundle: Bundle }> {
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/bundles/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** DELETE /v1/admin/bundles/:id — delete bundle. */
  async deleteBundle(id: string): Promise<{ deleted: boolean }> {
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/bundles/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  // ---- Users ----

  /** GET /v1/admin/users — list users with optional filter. */
  async listUsers(params?: {
    status?: 'active' | 'shadow' | 'banned' | 'retired';
    q?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ users: AdminUser[]; total: number; limit: number; offset: number }> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.q) query.set('q', params.q);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    const resp = await apiFetch(ADMIN_API_BASE, `/v1/admin/users${qs ? `?${qs}` : ''}`);
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },
};

// ---- Ops Trigger API (public Worker) ----

export const opsApi = {
  /**
   * POST /v1/admin/cache/purge — reset the Workers Cache.
   *
   * The ONLY place the purge API is reachable on a deliberate action: per-request invalidation is
   * the version counter, and this exists for bulk events (a city seed, an import, a repair) that
   * are rare and human-triggered. Exactly one of [purgeEverything] / [pathPrefixes] must be given.
   *
   * `pathPrefixes` entries must start with `/v1/nearby/`; the Worker rejects anything else, and
   * that scoping is what lets a city seed avoid cold-starting every other city's tiles.
   */
  async purgeCache(opts: { purgeEverything?: boolean; pathPrefixes?: string[] }): Promise<{
    success: boolean;
    purged: { purgeEverything?: boolean; pathPrefixes?: string[] };
  }> {
    const resp = await apiFetch(OPS_API_BASE, '/v1/admin/cache/purge', {
      method: 'POST',
      body: JSON.stringify(opts),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** POST /v1/admin/leaderboard/build — rebuild all leaderboards. */
  async rebuildLeaderboards(): Promise<{ compiled: number; scopes: number; flags_set: number }> {
    const resp = await apiFetch(OPS_API_BASE, '/v1/admin/leaderboard/build', { method: 'POST' });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** POST /v1/admin/profile-profanity/trigger — run profanity scan. */
  async triggerProfanityScan(fullScan = false): Promise<{ flagged: number; fullScan: boolean }> {
    const query = fullScan ? '?full_scan=true' : '';
    const resp = await apiFetch(OPS_API_BASE, `/v1/admin/profile-profanity/trigger${query}`, { method: 'POST' });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** POST /v1/admin/ai-moderation/trigger — run AI moderation review. */
  async triggerAiModeration(): Promise<{ triggered: boolean; reviewed: number; autoHidden: number }> {
    const resp = await apiFetch(OPS_API_BASE, '/v1/admin/ai-moderation/trigger', { method: 'POST' });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  // ---- Mascot Dialogs (#7.14 CMS) ----

  /** GET /v1/mascot/admin/dialogs — list all dialogs (paginated). */
  async listMascotDialogs(params?: {
    locale?: string;
    trigger?: string;
    active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ dialogs: MascotDialog[]; limit: number; offset: number }> {
    const query = new URLSearchParams();
    if (params?.locale) query.set('locale', params.locale);
    if (params?.trigger) query.set('trigger', params.trigger);
    if (params?.active) query.set('active', '1');
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    const qs = query.toString();
    const resp = await apiFetch(OPS_API_BASE, `/v1/mascot/admin/dialogs${qs ? `?${qs}` : ''}`);
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** GET /v1/mascot/admin/dialogs/:id — get single dialog. */
  async getMascotDialog(id: string): Promise<{ dialog: MascotDialog }> {
    const resp = await apiFetch(OPS_API_BASE, `/v1/mascot/admin/dialogs/${encodeURIComponent(id)}`);
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** POST /v1/mascot/admin/dialogs — create dialog. */
  async createMascotDialog(body: CreateMascotDialogBody): Promise<{ ok: boolean; id: string }> {
    const resp = await apiFetch(OPS_API_BASE, '/v1/mascot/admin/dialogs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** PUT /v1/mascot/admin/dialogs/:id — update dialog. */
  async updateMascotDialog(id: string, body: Partial<CreateMascotDialogBody>): Promise<{ ok: boolean; id: string }> {
    const resp = await apiFetch(OPS_API_BASE, `/v1/mascot/admin/dialogs/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** DELETE /v1/mascot/admin/dialogs/:id — deactivate (soft delete). */
  async deleteMascotDialog(id: string): Promise<{ ok: boolean; id: string }> {
    const resp = await apiFetch(OPS_API_BASE, `/v1/mascot/admin/dialogs/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** POST /v1/mascot/admin/dialogs/:id/activate — activate dialog. */
  async activateMascotDialog(id: string): Promise<{ ok: boolean; id: string; is_active: number }> {
    const resp = await apiFetch(OPS_API_BASE, `/v1/mascot/admin/dialogs/${encodeURIComponent(id)}/activate`, {
      method: 'POST',
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** POST /v1/mascot/admin/dialogs/:id/deactivate — deactivate dialog. */
  async deactivateMascotDialog(id: string): Promise<{ ok: boolean; id: string; is_active: number }> {
    const resp = await apiFetch(OPS_API_BASE, `/v1/mascot/admin/dialogs/${encodeURIComponent(id)}/deactivate`, {
      method: 'POST',
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },

  /** PATCH /v1/admin/users/:uid/status — ban or unban a user. */
  async setUserStatus(uid: string, status: 'banned' | 'active'): Promise<{
    uid: string;
    prev_status: string;
    new_status: string;
    applied: boolean;
  }> {
    const resp = await apiFetch(OPS_API_BASE, `/v1/admin/users/${encodeURIComponent(uid)}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    if (!resp.ok) throw await parseError(resp);
    return resp.json();
  },
};

// ---- Types ----

/**
 * Cache-health figures for the purgeless-invalidation surface, sourced from `eat_cache_versions`
 * (the per-cell version counter) and `eat_ops_counters` (purge outcomes).
 *
 * `purge_failure_count > 0` is the one number an operator should act on: it means a cache reset
 * reported failure, so the edge may still be serving pre-seed content.
 */
export interface CacheHealth {
  purge_success_count: number;
  purge_failure_count: number;
  purge_last_success_ms: number;
  purge_last_failure_ms: number;
  /** Cells that have held at least one venue — i.e. cells with a version row. */
  versioned_cells: number;
  /** Cells whose version moved in the last 24h: "is the version system alive?". */
  cells_bumped_24h: number;
}

export interface DashboardData {
  active_event_count: number;
  cache_health: CacheHealth;
  cosmetic_sales: {
    total_cosmetics: number;
    total_sold: number;
    active_cosmetics: number;
    top_cosmetics: { id: string; type: string; emoji: string; stock_sold: number; stock_limit: number | null }[];
  };
  premium: {
    active_premium: number;
    total_users_with_premium: number;
    total_active_users: number;
    conversion_rate: number;
  };
  liveops: {
    total_participants: number;
    completed: number;
    total_bonus_awarded: number;
    completion_rate: number;
  };
  dau_graph: { date_text: string; date_ms: number; dau: number; new_users: number; premium_active: number }[];
  timestamp: number;
}

export interface LiveopsEvent {
  id: string;
  title: string;
  description: string | null;
  venue_ids: string[];
  bonus_gs: number;
  premium_bonus_gs: number;
  premium_only: boolean;
  start_ms: number;
  end_ms: number;
  is_active: boolean;
  target_type: 'venue_set' | 'dish_set' | 'multi_dish' | 'multiplier';
  dish_targets: { dish_name: string; required_count: number }[] | null;
  // Phase A6 unification — multiplier + UI fields
  xp_multiplier: number;
  gs_multiplier: number;
  region_filter: string | null;
  emoji: string | null;
  accent_argb: number | null;
  participant_count: number;
}

export interface CreateLiveopsBody {
  id?: string;
  title: string;
  description?: string;
  target_type: 'venue_set' | 'dish_set' | 'multi_dish' | 'multiplier';
  venue_ids?: string[];
  dish_targets?: { dish_name: string; required_count: number }[];
  bonus_gs?: number;
  premium_bonus_gs?: number;
  premium_only?: boolean;
  start_ms?: number;
  end_ms?: number;
  // Phase A6 unification — multiplier fields
  xp_multiplier?: number;
  gs_multiplier?: number;
  region_filter?: string;
  emoji?: string;
  accent_argb?: number;
}

export interface LiveopsPerformance {
  event_id: string;
  total_participants: number;
  completed: number;
  completion_rate: number;
  total_bonus_awarded: number;
  avg_completion_ms: number | null;
  is_active: boolean;
  start_ms: number;
  end_ms: number;
}

export interface Cosmetic {
  id: string;
  type: string;
  emoji: string;
  accent_argb: number;
  premium_only: number;
  unlock_level: number;
  sort_order: number;
  acquire: string;
  iap_product: string | null;
  prestige_rule: string | null;
  stock_limit: number | null;
  stock_sold: number;
  is_active: number;
  display_context: string;
}

export interface CreateCosmeticBody {
  id: string;
  type: string;
  emoji: string;
  accent_argb?: number;
  premium_only?: boolean;
  unlock_level?: number;
  sort_order?: number;
  acquire?: string;
  iap_product?: string;
  prestige_rule?: string;
  stock_limit?: number;
  display_context?: string;
}

export interface Bundle {
  id: string;
  name: string;
  iap_product: string;
  cosmetic_ids: string[];
  discount_percent: number;
  is_active: number;
  available_from_ms: number | null;
  available_until_ms: number | null;
}

export interface CreateBundleBody {
  id: string;
  name: string;
  iap_product: string;
  cosmetic_ids: string[];
  discount_percent?: number;
  available_from_ms?: number;
  available_until_ms?: number;
}

// ---- Mascot Dialog Types (#7.14 CMS) ----

export interface MascotDialog {
  id: string;
  character: string; // 'chef_bro' | 'the_critic' | 'banter'
  trigger_type: string;
  trigger_conditions: string; // JSON
  locale: string;
  text_template: string;
  mood: string;
  animation: string | null;
  position_hint: string;
  cta_label: string | null;
  cta_action: string | null;
  cta_payload: string | null;
  variant: string;
  version: string;
  is_active: number;
  valid_from_ms: number;
  valid_until_ms: number;
  priority: number;
  sponsor_id: string | null;
  created_at_ms: number;
  updated_at_ms: number;
}

export interface CreateMascotDialogBody {
  id: string;
  character: string;
  trigger_type: string;
  trigger_conditions?: string;
  locale: string;
  text_template: string;
  mood?: string;
  animation?: string | null;
  position_hint?: string;
  cta_label?: string | null;
  cta_action?: string | null;
  cta_payload?: string | null;
  variant?: string;
  version?: string;
  is_active?: number;
  valid_from_ms?: number;
  valid_until_ms?: number;
  priority?: number;
  sponsor_id?: string | null;
}

// ---- User Management Types (TODO 5) ----

export interface AdminUser {
  uid: string;
  display_name: string | null;
  nickname: string | null;
  auth_provider: string;
  status: 'active' | 'shadow' | 'banned' | 'retired';
  shadow_reason: string | null;
  shadow_expires_at_ms: number | null;
  shadowed_at_ms: number | null;
  created_at_ms: number;
  last_seen_ms: number;
  is_public: number;
  locale: string;
  profile_updated_at_ms: number;
}
